import type { RowData } from "@tanstack/react-table";
import type { ViewTableInstance } from "./table";
import { getLeafColumnHeaders } from "./getLeafColumnHeaders";
import { isFillerColumn } from "./fillerColumns";

interface ColumnTrackInput {
    minSize?: number;
    maxSize?: number;
    // Live size from TanStack for this table instance.
    columnSize: number;
    // Size persisted on the view, when the column has one.
    viewSize?: number;
    isFiller: boolean;
    // CSS length a filler collapses to, so it can follow the table's edge inset.
    fillerMinWidth?: string;
    isUserResized: boolean;
    isActivelyResizing: boolean;
}

export const getColumnTrack = ({
    minSize,
    maxSize,
    columnSize,
    viewSize,
    isFiller,
    fillerMinWidth,
    isUserResized,
    isActivelyResizing,
}: ColumnTrackInput): string => {
    const resolvedMinSize = minSize ?? 0;

    if (isFiller) {
        return `minmax(${fillerMinWidth ?? "0px"}, auto)`;
    }

    // No persisted size and no local resize: let the grid size the column inside
    // the config's bounds. Without a maxSize the column grows with the available
    // space instead.
    if (!viewSize && !isUserResized) {
        if (maxSize === undefined) {
            return `minmax(${resolvedMinSize}px, 1fr)`;
        }
        return `minmax(${resolvedMinSize}px, ${maxSize}px)`;
    }

    // While this instance is dragging the column, follow the cursor with the live
    // local size. Otherwise prefer the view's explicit size, so sibling tables
    // sharing one view (including ones previously dragged, whose local
    // columnSizing is now stale) stay column-aligned. Either way the result is
    // clamped to the column config's bounds — those are the source of truth for
    // how wide a column may get, so a persisted size from an older, wider or
    // narrower config cannot escape the current one.
    const rawSize = isActivelyResizing ? columnSize : (viewSize ?? columnSize);
    const clampedSize = Math.min(
        maxSize ?? Number.POSITIVE_INFINITY,
        Math.max(resolvedMinSize, rawSize),
    );

    return `${clampedSize}px`;
};

export const getGridTemplateColumns = <TData extends RowData>({
    table,
    viewSizesByColumnId,
    userResizedColumnIds,
    activelyResizingColumnId,
}: {
    table: Pick<ViewTableInstance<TData>, "getLeafHeaders">;
    viewSizesByColumnId: Map<string, number | undefined>;
    userResizedColumnIds: ReadonlySet<string>;
    activelyResizingColumnId: string | null;
}): string =>
    getLeafColumnHeaders(table)
        .map((header) => {
            const { columnDef } = header.column;
            const columnId = String(columnDef.id);

            return getColumnTrack({
                minSize: columnDef.minSize,
                maxSize: columnDef.maxSize,
                columnSize: header.column.getSize(),
                viewSize: viewSizesByColumnId.get(columnId),
                isFiller: isFillerColumn(columnId),
                fillerMinWidth: columnDef.meta?.fillerMinWidth,
                isUserResized: userResizedColumnIds.has(columnId),
                isActivelyResizing: activelyResizingColumnId === columnId,
            });
        })
        .join(" ");
